import { useState, useEffect, useRef } from "react";
import { Select } from "antd";
import { useOutletContext } from "react-router-dom";
import { listTasks, getTask, createTask, startTask, saveModel } from "../../../services/training";
import type { TrainingTask } from "../../../services/training";
import { ensurePermission } from "../../../utils/permissions";
import { formatBeijingDateTime } from "../../../utils/datetime";
import RIcon from "../../../components/RIcon";

// ─── Constants ────────────────────────────────────────────────────────────────

const modelTypes = [
  { id: "text-cls", name: "文本分类", icon: "ri-price-tag-3-line", desc: "文档类型、合规性分类" },
  { id: "ner", name: "命名实体识别", icon: "ri-scan-2-line", desc: "药品名、靶点、机构识别" },
  { id: "qa", name: "问答模型微调", icon: "ri-question-answer-line", desc: "领域知识问答增强" },
  { id: "summarize", name: "摘要生成", icon: "ri-file-reduce-line", desc: "文档核心内容提取" },
];

const statusConfig: Record<string, { color: string; icon: string; bg: string; label: string }> = {
  pending:   { color: "text-amber-600",   icon: "ri-time-line",            bg: "bg-amber-50",        label: "排队中" },
  training:  { color: "text-[#0EA5A0]",   icon: "ri-loader-4-line",        bg: "bg-[#0EA5A0]/10",    label: "训练中" },
  completed: { color: "text-emerald-600", icon: "ri-checkbox-circle-line", bg: "bg-emerald-50",      label: "已完成" },
  failed:    { color: "text-red-500",     icon: "ri-close-circle-line",    bg: "bg-red-50",          label: "已失败" },
  saved:     { color: "text-violet-600",  icon: "ri-save-line",            bg: "bg-violet-50",       label: "已保存" },
};

function getStatusCfg(status: string) {
  return statusConfig[status] ?? { color: "text-gray-500", icon: "ri-question-line", bg: "bg-gray-100", label: status };
}

// ─── Task Detail Modal ─────────────────────────────────────────────────────────

interface TaskDetailProps {
  task: TrainingTask;
  onClose: () => void;
  onRefresh: () => void;
}

function TaskDetailModal({ task, onClose, onRefresh }: TaskDetailProps) {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const sc = getStatusCfg(task.status);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  async function handleStart() {
    setBusy(true);
    try {
      await startTask(task.id);
      showToast("训练已启动");
      onRefresh();
    } catch {
      showToast("启动失败，请重试");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    setBusy(true);
    try {
      await saveModel(task.id);
      showToast("模型已保存");
      onRefresh();
    } catch {
      showToast("保存失败，请重试");
    } finally {
      setBusy(false);
    }
  }

  const rows: [string, string][] = [
    ["任务名称", task.name ?? "—"],
    ["模型类型", modelTypes.find(m => m.id === task.model_type)?.name ?? task.model_type ?? "—"],
    ["训练轮数", `${task.epochs} Epochs`],
    ["学习率", task.learning_rate],
    ["Batch Size", String(task.batch_size)],
    ["数据量", task.sample_count != null ? `${task.sample_count} 条` : "—"],
    ["X 文件", task.x_file_name ?? "—"],
    ["Y 文件", task.y_file_name ?? "—"],
    ["Loss", task.result_loss ?? "—"],
    ["准确率", task.result_accuracy ? `${task.result_accuracy}` : "—"],
    ["训练时长", task.training_time ?? "—"],
    ["创建时间", task.created_at ? formatBeijingDateTime(task.created_at) : "—"],
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0EA5A0]/10 flex items-center justify-center">
              <RIcon name="cpu-line" className="text-[#0EA5A0] text-base" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1A2B3C]">{task.name ?? "训练任务"}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 mt-0.5 ${sc.bg} ${sc.color}`}>
                <i className={`${sc.icon} text-xs ${task.status === "training" ? "animate-spin" : ""}`} />
                {sc.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-[#718096] hover:bg-gray-100 rounded-lg cursor-pointer flex-shrink-0">
            <RIcon name="close-line" />
          </button>
        </div>

        {/* Progress bar */}
        {(task.status === "training" || task.status === "completed" || task.status === "saved") && (
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between text-xs text-[#718096] mb-1">
              <span>训练进度</span><span>{task.progress}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${task.status === "completed" || task.status === "saved" ? "bg-emerald-400" : "bg-[#0EA5A0]"}`}
                style={{ width: `${task.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Detail rows */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="bg-[#F4F8FB] rounded-xl divide-y divide-gray-100">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-4 py-2.5 text-xs">
                <span className="text-[#718096]">{k}</span>
                <span className="text-[#1A2B3C] font-medium text-right max-w-[60%] truncate">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex items-center justify-end gap-2">
          {(task.status === "pending" || task.status === "failed") && (
            <button
              onClick={handleStart}
              disabled={busy}
              className="px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] disabled:opacity-60 text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
            >
              {busy ? "启动中…" : "开始训练"}
            </button>
          )}
          {task.status === "completed" && (
            <button
              onClick={handleSave}
              disabled={busy}
              className="px-4 h-9 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
            >
              {busy ? "保存中…" : "保存模型"}
            </button>
          )}
          <button onClick={onClose} className="px-4 h-9 border border-gray-200 text-sm text-[#718096] hover:border-gray-300 rounded-lg cursor-pointer">关闭</button>
        </div>

        {toast && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-[#1A2B3C] text-white text-xs px-4 py-2 rounded-xl shadow-lg whitespace-nowrap">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── New Training Modal ────────────────────────────────────────────────────────

function NewTrainingModal({ onClose, onCreated, user, showToast }: { onClose: () => void; onCreated: () => void; user: import("../../../services/auth").UserInfo | null; showToast: (msg: string) => void; }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState("text-cls");
  const [taskName, setTaskName] = useState("");
  const [xFile, setXFile] = useState<File | null>(null);
  const [yFile, setYFile] = useState<File | null>(null);
  const [epochs, setEpochs] = useState(10);
  const [learningRate, setLearningRate] = useState("0.001");
  const [batchSize, setBatchSize] = useState(32);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!ensurePermission(user, "training:write", (msg) => { setError(msg); showToast(msg); })) return;
    if (!xFile || !yFile) { setError("请上传 X 文件和 Y 文件"); return; }
    setError(null);
    setSubmitting(true);
    try {
      const result = await createTask(xFile, yFile, selectedType, epochs, learningRate, batchSize);
      await startTask(result.id);
      setSubmitted(true);
      setTimeout(() => { onCreated(); onClose(); }, 1500);
    } catch {
      setError("提交失败，请检查文件格式后重试");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-[#1A2B3C]">新建训练任务</h3>
            <p className="text-xs text-[#718096] mt-0.5">步骤 {step} / 3</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-[#718096] hover:bg-gray-100 rounded-lg cursor-pointer"><RIcon name="close-line" /></button>
        </div>

        <div className="overflow-y-auto flex-1">
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-5">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${s < step ? "bg-[#0EA5A0] text-white" : s === step ? "bg-[#1A2B3C] text-white" : "bg-gray-100 text-[#718096]"}`}>
                  {s < step ? <RIcon name="check-line" className="text-xs" /> : s}
                </div>
                <span className="text-xs text-[#718096]">{s === 1 ? "选择模型类型" : s === 2 ? "上传数据文件" : "训练参数"}</span>
                {s < 3 && <div className={`flex-1 h-px w-8 ${s < step ? "bg-[#0EA5A0]" : "bg-gray-200"}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-[#718096] mb-3">请选择要训练的模型任务类型</p>
              {modelTypes.map((mt) => (
                <button key={mt.id} onClick={() => setSelectedType(mt.id)} className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer text-left ${selectedType === mt.id ? "border-[#0EA5A0] bg-[#0EA5A0]/5" : "border-gray-200 hover:border-gray-300"}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${selectedType === mt.id ? "bg-[#0EA5A0]/15" : "bg-[#F4F8FB]"}`}>
                    <i className={`${mt.icon} ${selectedType === mt.id ? "text-[#0EA5A0]" : "text-[#718096]"} text-base`} />
                  </div>
                  <div><p className="text-sm font-medium text-[#1A2B3C]">{mt.name}</p><p className="text-xs text-[#718096]">{mt.desc}</p></div>
                  {selectedType === mt.id && <div className="ml-auto w-5 h-5 flex items-center justify-center"><RIcon name="checkbox-circle-fill" className="text-[#0EA5A0] text-lg" /></div>}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">任务名称</label>
                <input type="text" value={taskName} onChange={(e) => setTaskName(e.target.value)} placeholder="例：药品说明书解析模型 v2.1" className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0] text-[#1A2B3C]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">X 文件（特征数据）</label>
                <input type="file" onChange={(e) => setXFile(e.target.files?.[0] ?? null)} className="w-full text-sm text-[#4A5568] file:mr-3 file:px-3 file:py-1.5 file:border-0 file:rounded-lg file:bg-[#0EA5A0]/10 file:text-[#0EA5A0] file:text-xs file:cursor-pointer cursor-pointer" />
                {xFile && <p className="text-xs text-emerald-600 mt-1">已选择：{xFile.name}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">Y 文件（标签数据）</label>
                <input type="file" onChange={(e) => setYFile(e.target.files?.[0] ?? null)} className="w-full text-sm text-[#4A5568] file:mr-3 file:px-3 file:py-1.5 file:border-0 file:rounded-lg file:bg-[#0EA5A0]/10 file:text-[#0EA5A0] file:text-xs file:cursor-pointer cursor-pointer" />
                {yFile && <p className="text-xs text-emerald-600 mt-1">已选择：{yFile.name}</p>}
              </div>
            </div>
          )}

          {step === 3 && !submitted && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">训练轮数 (Epochs)</label>
                  <input type="number" value={epochs} onChange={(e) => setEpochs(Number(e.target.value))} min={1} max={100} className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0] text-[#1A2B3C]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">学习率</label>
                  <input type="text" value={learningRate} onChange={(e) => setLearningRate(e.target.value)} className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0] text-[#1A2B3C]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1A2B3C] mb-1.5 block">Batch Size</label>
                  <Select
                    style={{ width: "100%" }}
                    value={batchSize}
                    onChange={(v) => setBatchSize(Number(v))}
                    options={[
                      { value: 16, label: "16" },
                      { value: 32, label: "32" },
                      { value: 64, label: "64" },
                    ]}
                  />
                </div>
              </div>
              <div className="bg-[#F4F8FB] rounded-xl p-3.5">
                <p className="text-xs font-medium text-[#1A2B3C] mb-2">训练摘要</p>
                <div className="space-y-1.5">
                  {([["模型类型", modelTypes.find(m => m.id === selectedType)?.name ?? "-"], ["任务名称", taskName || "未命名"], ["X 文件", xFile?.name ?? "—"], ["Y 文件", yFile?.name ?? "—"], ["训练轮数", `${epochs} Epochs`]] as [string, string][]).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs"><span className="text-[#718096]">{k}</span><span className="text-[#1A2B3C] font-medium">{v}</span></div>
                  ))}
                </div>
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </div>
          )}

          {submitted && (
            <div className="py-8 text-center">
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3"><RIcon name="checkbox-circle-line" className="text-2xl text-emerald-500" /></div>
              <p className="text-sm font-semibold text-[#1A2B3C]">训练任务已提交</p>
              <p className="text-xs text-[#718096] mt-1">任务已加入队列，训练完成后将发送通知</p>
            </div>
          )}

          {!submitted && (
            <div className="flex justify-between mt-5">
              <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="px-4 h-9 border border-gray-200 text-sm text-[#718096] hover:border-gray-300 rounded-lg cursor-pointer whitespace-nowrap">{step === 1 ? "取消" : "上一步"}</button>
              <button
                disabled={submitting}
                onClick={() => step < 3 ? setStep(step + 1) : handleSubmit()}
                className="px-5 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] disabled:opacity-60 text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
              >
                {step < 3 ? "下一步" : submitting ? "提交中…" : "开始训练"}
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ModelTrainingPage() {
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TrainingTask | null>(null);
  const [tasks, setTasks] = useState<TrainingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  async function loadTasks() {
    try {
      const data = await listTasks();
      setTasks(data);
    } catch {
      // silently keep existing data on refresh errors
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTasks();
    intervalRef.current = setInterval(() => {
      loadTasks();
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const filtered = tasks.filter(t =>
    !search || (t.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const completedCount = tasks.filter(t => t.status === "completed" || t.status === "saved").length;
  const trainingCount = tasks.filter(t => t.status === "training").length;

  const accuracyTasks = tasks.filter(t => t.result_accuracy != null);
  const avgAccuracy = accuracyTasks.length > 0
    ? (accuracyTasks.reduce((sum, t) => sum + parseFloat(t.result_accuracy!), 0) / accuracyTasks.length).toFixed(1) + "%"
    : "—";

  const stats = [
    { label: "训练任务总数", value: tasks.length, icon: "ri-cpu-line", color: "text-[#0EA5A0]", bg: "bg-[#0EA5A0]/10" },
    { label: "已完成", value: completedCount, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "训练中", value: trainingCount, icon: "ri-loader-4-line", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "平均准确率", value: avgAccuracy, icon: "ri-bar-chart-line", color: "text-violet-500", bg: "bg-violet-50" },
  ];

  function handleTaskClick(task: TrainingTask) {
    setSelectedTask(task);
  }

  async function handleDeleteClick(e: React.MouseEvent, _taskId: number) {
    e.stopPropagation();
    if (!ensurePermission(user, "training:write", showToast)) return;
    showToast("暂不支持删除");
  }

  async function handleStartClick(e: React.MouseEvent, taskId: number) {
    e.stopPropagation();
    if (!ensurePermission(user, "training:write", showToast)) return;
    try {
      await startTask(taskId);
      await loadTasks();
    } catch {
      // ignore
    }
  }

  async function handleSaveClick(e: React.MouseEvent, taskId: number) {
    e.stopPropagation();
    if (!ensurePermission(user, "training:write", showToast)) return;
    try {
      await saveModel(taskId);
      await loadTasks();
    } catch {
      // ignore
    }
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-[#1A2B3C]">模型训练</h1>
          <p className="text-sm text-[#718096] mt-0.5">基于企业数据微调专属 AI 模型，提升知识检索与问答精度</p>
        </div>
        <button onClick={() => ensurePermission(user, "training:write", showToast) && setShowNewModal(true)} className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <RIcon name="add-line" />新建训练任务
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
              <i className={`${s.icon} ${s.color} text-lg`} />
            </div>
            <div>
              <p className="text-xs text-[#718096]">{s.label}</p>
              <p className="text-lg font-bold text-[#1A2B3C]">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-[#1A2B3C]">训练任务列表</span>
          <div className="flex items-center gap-2">
            <p className="text-xs text-[#718096]"><RIcon name="mouse-line" className="mr-1" />点击任务行可查看详情</p>
            <div className="flex items-center gap-2 bg-[#F4F8FB] rounded-lg px-3 h-8 ml-2">
              <RIcon name="search-line" className="text-[#718096] text-sm" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索任务..." className="text-xs bg-transparent outline-none text-[#4A5568] w-28 placeholder:text-gray-400" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#718096]">
            <RIcon name="loader-4-line" className="animate-spin text-xl mr-2" />加载中…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-[#718096] text-sm">暂无训练任务</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((task) => {
              const sc = getStatusCfg(task.status);
              const accuracy = task.result_accuracy ? parseFloat(task.result_accuracy) : null;
              const loss = task.result_loss ? parseFloat(task.result_loss) : null;
              const showProgress = task.status === "training" || task.status === "completed" || task.status === "saved";
              const duration = task.training_time ?? (task.status === "training" ? "运行中" : task.status === "pending" ? "等待中" : "—");
              const dataSize = task.sample_count != null ? `${task.sample_count} 条` : "—";
              const modelLabel = modelTypes.find(m => m.id === task.model_type)?.name ?? task.model_type ?? "—";
              const startTime = task.created_at ? formatBeijingDateTime(task.created_at) : "—";

              return (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="px-5 py-4 flex items-center gap-4 hover:bg-[#F4F8FB]/80 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-[#1A2B3C] truncate">{task.name ?? `任务 #${task.id}`}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${sc.bg} ${sc.color}`}>
                        <i className={`${sc.icon} text-xs ${task.status === "training" ? "animate-spin" : ""}`} />
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#718096]">
                      类型：{modelLabel} · 数据量：{dataSize} · 创建：{startTime}
                    </p>
                    {showProgress && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${task.status === "completed" || task.status === "saved" ? "bg-emerald-400" : "bg-[#0EA5A0]"}`} style={{ width: `${task.progress}%` }} />
                        </div>
                        <span className="text-xs text-[#718096] w-8 text-right">{task.progress}%</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {accuracy !== null && !isNaN(accuracy) && (
                      <div className="text-right"><p className="text-xs text-[#718096]">准确率</p><p className="text-sm font-bold text-emerald-600">{accuracy.toFixed(1)}%</p></div>
                    )}
                    {loss !== null && !isNaN(loss) && (
                      <div className="text-right"><p className="text-xs text-[#718096]">Loss</p><p className="text-sm font-bold text-[#1A2B3C]">{loss.toFixed(4)}</p></div>
                    )}
                    <div className="text-right"><p className="text-xs text-[#718096]">耗时</p><p className="text-sm font-medium text-[#4A5568]">{duration}</p></div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {task.status === "completed" && (
                        <button onClick={(e) => handleSaveClick(e, task.id)} className="w-7 h-7 flex items-center justify-center text-[#718096] hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors cursor-pointer" title="保存模型"><RIcon name="save-line" className="text-sm" /></button>
                      )}
                      {(task.status === "pending" || task.status === "failed") && (
                        <button onClick={(e) => handleStartClick(e, task.id)} className="w-7 h-7 flex items-center justify-center text-[#718096] hover:text-[#0EA5A0] hover:bg-[#0EA5A0]/10 rounded-lg transition-colors cursor-pointer" title="开始训练"><RIcon name="play-line" className="text-sm" /></button>
                      )}
                      <button onClick={(e) => handleDeleteClick(e, task.id)} className="w-7 h-7 flex items-center justify-center text-[#718096] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"><RIcon name="delete-bin-line" className="text-sm" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNewModal && <NewTrainingModal onClose={() => setShowNewModal(false)} onCreated={loadTasks} user={user} showToast={showToast} />}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onRefresh={() => { loadTasks(); setSelectedTask(null); }}
        />
      )}
    </div>
  );
}
